import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { FirebaseError } from 'firebase/app';
import type {
  AgentRequest,
  BetaAccessRequest,
  Chart,
  HealthcareAgentInfo,
  PatientInfo1,
} from '@usacsmideo/types';
import { patientAuthorizationConsentVersion } from '@usacsmideo/types';
import { Button } from '../buttons';
import { AgentConsentBlock } from './AgentConsentBlock';
import { auth } from '../../constants/firebaseModular';
import useChart from '../../hooks/useChart';
import { useMyAgents } from '../../hooks/useAgentRequests';
import { useUpdateChartSection } from '../../hooks/useUpdateChartSection';
import { inviteAgent, archiveAgentRequest } from '../../data/agents/handleAgentRequest';
import { resolveSlotStatus, type SlotStatus } from '../../data/agents/agentSlotConvergence';
import { requestBetaAccess } from '../../data/requestBetaAccess';
import useBetaRequest from '../../hooks/useBetaRequest';
import { AlternateAgentsManager } from './AlternateAgentsManager';

/** The beta feature gating the My MIDEO Family screen (CU-86aj8k2b9). */
const BETA_FEATURE = 'secureQrFlow';

/**
 * An empty HCA record. Removing the HCA overwrites the slot with this (same
 * setDoc(merge) path as any edit) so the slot resolves to "empty" and is reusable;
 * the archived agentRequest remains the audit trail. Not a hard delete.
 */
const EMPTY_HCA: HealthcareAgentInfo = {
  agent: '',
  email: '',
  primaryPhone: '',
  street: '',
  city: '',
  state: '',
  ZIP: '',
  country: '',
  agentToPatient: '',
  patientToAgent: '',
  hasPowerOfAttorney: '',
  agentRequestId: '',
};

/**
 * ECN invite ↔ chart convergence (CU-86aj2q4fc) — patient view of My MIDEO Family.
 *
 * Chart-slot-driven: the HCA's chart record (HEALTHCARE_AGENT_INFO) is the primary
 * entity; its agentRequest is matched by the slot's stamped `agentRequestId`
 * (validity by email match). The legacy "amber box" (chart HCA with no invite) is
 * RETIRED — that case is now just the `incomplete`/`invitable` slot state under the
 * unified gate. Demographics are edited only in the chart form (hcaInfo); the
 * invite fires here and stamps the link.
 *
 * Alternate management is delegated to the shared `AlternateAgentsManager`.
 */

function statusLabel(r: AgentRequest): string {
  switch (r.status) {
    case 'waiting':
      return 'Invitation sent — awaiting acceptance';
    case 'accepted':
      return 'Active — receiving alerts';
    default:
      return r.status;
  }
}

export function PatientFamilyView() {
  const uid = auth.currentUser?.uid ?? '';
  const navigate = useNavigate();
  const { partition, hasResolved } = useMyAgents();
  const chartQuery = useChart(uid, { enabled: !!uid });
  const chart = chartQuery.data as Chart | undefined;
  const { mutate: stampChart, mutateAsync: writeChart } = useUpdateChartSection();

  // Match the HCA chart slot to its request by the stamped id (all statuses, so
  // resolveSlotStatus can distinguish "no link" from "link to archived").
  const hcaRequestsById = React.useMemo(() => {
    const map = new Map<string, AgentRequest>();
    partition.all.filter((r) => r.agentType === 'HCA').forEach((r) => map.set(r.id, r));
    return map;
  }, [partition.all]);

  const hcaRecord = chart?.HEALTHCARE_AGENT_INFO as HealthcareAgentInfo | undefined;
  const hcaLinked = hcaRecord?.agentRequestId
    ? hcaRequestsById.get(hcaRecord.agentRequestId)
    : undefined;
  const hcaStatus: SlotStatus = resolveSlotStatus(hcaRecord, hcaLinked);

  // Patient names for the P1 consent template. PATIENT_INFO_1 is the canonical
  // patient profile (same source CardFront / WitnessSignatureArea read).
  const patientInfo = chart?.PATIENT_INFO_1 as PatientInfo1 | undefined;
  const patientFullName = `${patientInfo?.firstName ?? ''} ${patientInfo?.lastName ?? ''}`.trim();
  const patientFirstName = patientInfo?.firstName ?? '';

  // Consent assent must be captured before an HCA invite goes out (the backend
  // records authorizationToDisclose on create; this is the patient's assent, not a
  // drawn signature). We surface it inline only when the slot is invite-ready.
  const [assenting, setAssenting] = React.useState(false);
  const [assented, setAssented] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Beta gate: the My MIDEO Family interface is only available once the secure
  // QR flow is enabled for this patient. Absent flag is treated as not-enabled.
  const betaEnabled = chart?.STATUS?.useSecureQrFlow === true;
  const gated = !betaEnabled;

  // Only listen for the request record while gated (enabled patients never need
  // it). Drives the 3-state gate: no record → request, requested → pending,
  // declined → re-request.
  const betaRequestQuery = useBetaRequest(uid, BETA_FEATURE, {
    enabled: !!uid && gated,
  });
  const betaRequest = betaRequestQuery.data as BetaAccessRequest | null | undefined;
  const [requestingBeta, setRequestingBeta] = React.useState(false);

  const handleRequestBeta = async () => {
    setRequestingBeta(true);
    setError(null);
    try {
      await requestBetaAccess({ featureName: BETA_FEATURE });
      // The listener flips the gate to "pending" once the write lands.
    } catch {
      setError('We could not submit your request. Please try again or contact support.');
    } finally {
      setRequestingBeta(false);
    }
  };

  const goToHcaForm = () => {
    navigate({ to: '/$patientId/careTeam/hcaInfo', params: { patientId: uid } });
  };

  const handleInviteHca = async () => {
    if (!hcaRecord) return;
    if (!assented) {
      setError('Please confirm the authorization before sending the invitation.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Re-inviting an ORPHANED slot: archive the stale (email-mismatched) live
      // request first, so the abandoned email can't accept and we never leave two
      // live HCA invites for one patient.
      if (hcaStatus.state === 'orphaned' && hcaStatus.request) {
        await archiveAgentRequest(hcaStatus.request.id);
      }
      const res = await inviteAgent({
        agentType: 'HCA',
        recipientEmail: hcaRecord.email.trim(),
        recipientName: hcaRecord.agent.trim() || undefined,
        // Affirmation gate (P2.1/P2.2): the patient ticked the P1 consent (guarded
        // above). The version is derived from the SAME shared builder the backend
        // drift-checks against — never a local literal.
        consentAffirmed: true,
        consentVersion: patientAuthorizationConsentVersion(),
      });
      // Stamp the request id onto the chart on BOTH outcomes: the request doc
      // exists even when the email failed to send, so linking it lets the
      // orphan/re-invite affordance manage + resend it (and avoids a duplicate
      // request on retry).
      const stamped: HealthcareAgentInfo = { ...hcaRecord, agentRequestId: res.id };
      stampChart({ patientId: uid, docData: { HEALTHCARE_AGENT_INFO: stamped } });
      setAssenting(false);
      setAssented(false);
      // `ok` reflects DELIVERY, not doc creation (MIDEO-UNIFIED-18). A failed
      // send must not read as clean success — tell the patient the agent was
      // NOT emailed so they can verify the address and resend.
      if (res.ok === false || res.deliveryStatus === 'failed') {
        setError(
          `Invitation created, but we couldn't email ${hcaRecord.email.trim()}. ` +
            'Please verify the address and use Re-invite to try again.',
        );
        toast.error("We couldn't deliver the invitation email.");
      } else {
        toast.success('Invitation sent.');
      }
    } catch (err) {
      // A malformed email is rejected by the cloud function with
      // invalid-argument (the hard gate behind the form validation).
      if (err instanceof FirebaseError && err.code === 'functions/invalid-argument') {
        setError('That email address looks invalid. Please check it and try again.');
      } else {
        setError('We could not send the invitation. Please try again or contact support.');
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * Remove the HCA: archive any live invite (audit trail kept) and clear the slot
   * by OVERWRITING HEALTHCARE_AGENT_INFO to empty (same setDoc(merge) path as any
   * edit, not a hard delete). The slot then resolves to "empty" and is reusable.
   */
  const handleRemoveHca = async (id: string | undefined, name: string) => {
    const confirmed = window.confirm(
      `Remove ${name || 'this Healthcare Agent'}? This stops any alerts to them and clears their ` +
        `saved details from your chart. You can designate someone new afterward.`,
    );
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      if (id) {
        await archiveAgentRequest(id);
      }
      await writeChart({ patientId: uid, docData: { HEALTHCARE_AGENT_INFO: EMPTY_HCA } });
    } catch {
      setError('We could not remove this agent. Please try again or contact support.');
    } finally {
      setBusy(false);
    }
  };

  if (!hasResolved) {
    return (
      <div className="flex w-full max-w-3xl flex-col items-center gap-4 text-center">
        <p className="text-gray-600">Loading…</p>
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-3xl flex-col gap-6">
      <div className="text-center">
        <div className="flex items-center justify-center gap-2">
          <h1 className="text-2xl font-semibold text-sky-800">My MIDEO Family</h1>
          <span className="inline-flex items-center rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold text-sky-800">
            Beta
          </span>
        </div>
        <p className="mt-1 text-gray-600">
          Designate the people authorized to act on your behalf and receive your emergency
          notifications: one Healthcare Agent and up to two alternates.
        </p>
        <p className="mt-1 text-sm text-gray-500">
          This is a new feature we&rsquo;re rolling out gradually.
        </p>
      </div>

      {error && (
        <p className="rounded-md bg-red-50 p-3 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}

      {gated ? (
        /* Beta gate — the family interface is hidden until the secure QR flow
           is enabled for this patient. 3-state: none → request, requested →
           pending, declined → re-request. */
        <div className="flex flex-col items-center gap-3 rounded-md border border-sky-200 bg-sky-50 p-6 text-center">
          {betaRequest?.status === 'requested' ? (
            <>
              <p className="font-semibold text-sky-800">Request pending</p>
              <p className="text-sm text-gray-600">
                Thanks for your interest — we&rsquo;ve received your request and will be in touch
                when this feature is enabled for your account.
              </p>
            </>
          ) : (
            <>
              <p className="font-semibold text-sky-800">Request access to this beta feature</p>
              <p className="text-sm text-gray-600">
                My MIDEO Family lets you designate a Healthcare Agent and alternates to receive your
                emergency notifications. It&rsquo;s rolling out gradually — request access and
                we&rsquo;ll enable it for your account.
              </p>
              {betaRequest?.status === 'declined' && (
                <p className="text-sm text-amber-700">
                  Your previous request wasn&rsquo;t approved. You can request again.
                </p>
              )}
              <Button
                type="button"
                id="request-beta-access"
                textContent="Request access to Beta Feature"
                working={requestingBeta}
                workingText="Submitting…"
                disabled={requestingBeta}
                onClick={handleRequestBeta}
              />
            </>
          )}
        </div>
      ) : (
        <>
      {/* Healthcare Agent — unified chart-slot gate (no more amber box). */}
      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold text-gray-800">Healthcare Agent</h2>

        {hcaStatus.state === 'empty' ? (
          <Button
            type="button"
            id="add-hca"
            textContent="Add your Healthcare Agent"
            onClick={goToHcaForm}
          />
        ) : (
          <div className="flex w-full items-center justify-between rounded-md border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-left">
              <p className="font-semibold text-sky-800">{hcaRecord?.agent || 'Healthcare Agent'}</p>
              {hcaStatus.state === 'incomplete' && (
                <p className="text-sm text-amber-700">
                  Details incomplete — finish their chart record before inviting.
                </p>
              )}
              {hcaStatus.state === 'invitable' && (
                <p className="text-sm text-gray-600">Ready to invite to receive alerts.</p>
              )}
              {hcaStatus.state === 'orphaned' && (
                <p className="text-sm text-amber-700">
                  Their email changed — this invite no longer matches. Re-invite to restore alerts.
                </p>
              )}
              {hcaStatus.state === 'invited' && hcaStatus.request && (
                <p className="text-sm text-gray-600">{statusLabel(hcaStatus.request)}</p>
              )}
            </div>

            <div className="flex gap-2">
              {hcaStatus.state === 'incomplete' && (
                <Button
                  type="button"
                  id="add-details-hca"
                  textContent="Add details to invite"
                  onClick={goToHcaForm}
                />
              )}
              {(hcaStatus.state === 'invitable' || hcaStatus.state === 'orphaned') && !assenting && (
                <Button
                  type="button"
                  id={hcaStatus.state === 'orphaned' ? 'reinvite-hca' : 'invite-hca'}
                  textContent={hcaStatus.state === 'orphaned' ? 'Re-invite' : 'Invite to receive alerts'}
                  onClick={() => {
                    setAssented(false);
                    setError(null);
                    setAssenting(true);
                  }}
                />
              )}
              {!assenting && (
                <Button
                  type="button"
                  id={hcaStatus.request ? `remove-${hcaStatus.request.id}` : 'remove-hca'}
                  textContent="Remove"
                  working={busy}
                  workingText="Removing…"
                  disabled={busy}
                  classNameOverride="my-2 rounded-md border border-gray-300 px-4 py-2 text-xl text-gray-700 hover:bg-gray-100 sm:px-8 sm:py-3"
                  onClick={() =>
                    handleRemoveHca(hcaStatus.request?.id, hcaRecord?.agent ?? '')
                  }
                />
              )}
            </div>
          </div>
        )}

        {/* Consent authorization (P1 template) + affirmation, shown only when
            arming an HCA invite. The signed copy dictates "Send Invite" +
            "I agree to the terms and conditions" (in AgentConsentBlock). */}
        {assenting && (
          <div className="flex flex-col gap-3">
            <AgentConsentBlock
              vars={{
                patientFullName,
                patientFirstName,
                // Full agent name string in the first-name slot (chart has no
                // split first/last) — grammatically correct in P1, mirrors backend.
                agentFirstName: hcaRecord?.agent?.trim() ?? '',
                agentRole: 'Healthcare Agent',
              }}
              affirmed={assented}
              onAffirmChange={setAssented}
              disabled={busy}
            />
            <div className="flex gap-2">
              <Button
                type="button"
                id="send-invite-hca"
                textContent="Send Invite"
                working={busy}
                workingText="Sending…"
                disabled={busy || !assented}
                onClick={handleInviteHca}
              />
              <Button
                type="button"
                id="cancel-invite-hca"
                textContent="Cancel"
                disabled={busy}
                classNameOverride="my-4 rounded-md border border-gray-300 px-4 py-2 text-xl text-gray-700 hover:bg-gray-100 sm:px-8 sm:py-3"
                onClick={() => {
                  setAssenting(false);
                  setAssented(false);
                }}
              />
            </div>
          </div>
        )}
      </section>

      {/* Alternate Agents — shared with the careTeam alternateAgents screen. */}
      <AlternateAgentsManager />
        </>
      )}
    </div>
  );
}

export default PatientFamilyView;
